import { apiFetch, hasLiveApi } from '@/api/client'

/** Lab Mongo Game ids used by My P/L `game` (Lab Aggregate master-dashboard). */
export const LAB_MY_PNL_GAME_IDS = {
  sportsBook: '5b36053e46123555cca93993',
  liveCasino: '5a818572650cb46dd9b1a25a',
  cardGame: '5f311f236eae8b1e0f35e59a',
  premiumSportsbook: '60f5637b64b34916022db498',
  racing: '60ed93b341bae473d53ae139',
} as const

/** Lab / Aggregate-API page size for getMasterDashboardAggregator. */
export const MY_PNL_PAGE_SIZE = 50

export type MyPnlFilter = {
  from: string
  to: string
  subGame: string
  eventId?: string
  typeName?: string
  page?: number
}

export type MyPnlApiRow = {
  _id?: string
  eventId?: string
  eventName?: string
  gameName?: string
  subGame?: string
  typeName?: string
  matchName?: string
  username?: string
  createdAt?: string
  win?: number
  commission?: number
  netAmt?: number
  winINR?: number
  commissionINR?: number
  netAmtINR?: number
  betFairAmount?: number
  uplineCommission?: number
  downlineCommission?: number
  betCount?: number
  userCount?: number
}

export type MyPnlSum = {
  win: number
  commission: number
  netAmt: number
  winINR: number
  commissionINR: number
  netAmtINR: number
  betFairAmount: number
}

/** Aggregate-API Standard `{ status, message, data }` for list. */
export type MyPnlListResponse = {
  status: number
  message: string
  data: {
    results: MyPnlApiRow[]
    totalSum: MyPnlSum
    totalItems: number
    maxRow: number
  }
  /** @deprecated Lab mypl shape — tolerated during rollout. */
  value?: boolean
}

export type MyPnlEventOption = { value: string; label: string }

export type MyPnlDrillRow = {
  id: string
  label: string
  eventName: string
  win: number
  commission: number
  netAmt: number
  winInr: number
  commissionInr: number
  netAmtInr: number
  betFairAmount: number
  uplineCommission: number
  downlineCommission: number
  betCount: number
  userCount: number
}

export type MyPnlEventRow = {
  id: string
  settledAt: string
  gameName: string
  subGame: string
  eventName: string
  eventId?: string
  win: number
  commission: number
  netAmt: number
  winInr: number
  commissionInr: number
  netAmtInr: number
  betFairAmount: number
  uplineCommission: number
  downlineCommission: number
  userCount: number
  betCount: number
  /** Inline Market/User drill row under an event (Lab `market-row` / `user-row`). */
  drill?: boolean
  /** Parent event id — drill rows sort with this event (stable sort keeps them under it). */
  drillParentId?: string
}

/** Resolve the event row a table row sorts as (drill → parent). */
export function myPnlSortTarget(
  row: MyPnlEventRow,
  byId: Map<string, MyPnlEventRow>,
): MyPnlEventRow {
  if (row.drill && row.drillParentId) return byId.get(row.drillParentId) ?? row
  return row
}

/** Picker values for name-only events (no BF / settlement event id). */
export const MY_PNL_EVENT_NAME_PREFIX = 'n:'

/**
 * Keep only rows that belong to the applied Event filter.
 * Defense in depth when a prior list paint / cache still carries another event
 * (UI showed India under a LASK chip while pager said 1 of 1).
 */
export function myPnlRowMatchesEventFilter(
  row: Pick<MyPnlEventRow, 'id' | 'eventId' | 'eventName' | 'drill' | 'drillParentId'>,
  eventKey: string,
  parentIds?: Set<string>,
): boolean {
  if (!eventKey || eventKey === 'All') return true
  if (row.drill) {
    if (!row.drillParentId) return false
    return parentIds?.has(row.drillParentId) ?? false
  }
  if (eventKey.startsWith(MY_PNL_EVENT_NAME_PREFIX)) {
    const name = eventKey.slice(MY_PNL_EVENT_NAME_PREFIX.length).trim()
    if (!name) return true
    return (
      row.eventName.trim() === name ||
      row.id.trim() === name ||
      row.eventId?.trim() === name
    )
  }
  const key = eventKey.trim()
  return (
    row.eventId?.trim() === key ||
    row.id.trim() === key ||
    row.eventName.trim() === key
  )
}

/** Filter list (+ optional drills) down to the active Event chip. */
export function filterMyPnlRowsForEvent<T extends MyPnlEventRow>(
  rows: T[],
  eventKey: string,
): T[] {
  if (!eventKey || eventKey === 'All') return rows
  const parents = rows.filter(
    (r) => !r.drill && myPnlRowMatchesEventFilter(r, eventKey),
  )
  const parentIds = new Set(parents.map((r) => r.id))
  return rows.filter((r) => myPnlRowMatchesEventFilter(r, eventKey, parentIds))
}

const ZERO_SUM: MyPnlSum = {
  win: 0,
  commission: 0,
  netAmt: 0,
  winINR: 0,
  commissionINR: 0,
  netAmtINR: 0,
  betFairAmount: 0,
}

/**
 * Lab Aggregate old `formData.game` + subGame → game id.
 * Lab UI defaults `formData.game` to SportsBook id, but MyPL still merges
 * Live Casino when `subGame` is All — send `game: "All"` so Aggregate-API
 * uses the inclusive product path (not SportsBook-only exclusion).
 */
export function labMyPnlGameId(subGame: string): string {
  switch (subGame) {
    case 'All':
      return 'All'
    case 'Card Game':
    case 'CardGame':
      return LAB_MY_PNL_GAME_IDS.cardGame
    case 'Live Casino':
      return LAB_MY_PNL_GAME_IDS.liveCasino
    case 'Premium SportsBook':
    case 'Premium-SportsBook':
      return LAB_MY_PNL_GAME_IDS.premiumSportsbook
    case 'Cricket':
    case 'Tennis':
    case 'Soccer':
      return LAB_MY_PNL_GAME_IDS.sportsBook
    case 'Horse Racing':
    case 'Greyhound Racing':
      return LAB_MY_PNL_GAME_IDS.racing
    case 'Premium Bookmaker':
    case 'Premium Fancy':
      return LAB_MY_PNL_GAME_IDS.sportsBook
    default:
      return LAB_MY_PNL_GAME_IDS.sportsBook
  }
}

/** Option value prefix when settlement has event_name but no event_id. */
function applyEventKey(body: Record<string, unknown>, eventKey: string): void {
  if (!eventKey || eventKey === 'All') return
  if (eventKey.startsWith(MY_PNL_EVENT_NAME_PREFIX)) {
    body.eventName = eventKey.slice(MY_PNL_EVENT_NAME_PREFIX.length)
    return
  }
  // Picker stores names as `n:…`. Bare keys are row eventIds (BF `1.xxx`, uuid, etc.).
  // Date-grouped casino/card labels without an id still go as eventName.
  if (/CardGame|LiveCasino/i.test(eventKey) || /\s/.test(eventKey)) {
    body.eventName = eventKey
    return
  }
  body.eventId = eventKey
}

/**
 * Lab Aggregate `NavigationService.getMasterDashboardAggregator` body
 * (mypl `Account/getMasterDashboardAggregator`).
 */
export function myPnlRequestBody(
  filter: MyPnlFilter,
  extras: { eventName?: string } = {},
): Record<string, unknown> {
  const subGame = filter.subGame?.trim() || 'All'
  const body: Record<string, unknown> = {
    fromDate: filter.from.slice(0, 10),
    toDate: filter.to.slice(0, 10),
    game: labMyPnlGameId(subGame),
    subGame,
    page: Math.max(1, Math.floor(filter.page ?? 1)),
    bookmakerSessionFlag: 'all',
  }
  applyEventKey(body, filter.eventId?.trim() ?? '')
  const typeName = filter.typeName?.trim()
  if (typeName && typeName !== 'All') body.typeName = typeName
  const eventName = extras.eventName?.trim()
  if (eventName && eventName !== 'All') body.eventName = eventName
  return body
}

function n(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const x = Number(v)
    if (Number.isFinite(x)) return x
  }
  return 0
}

function s(v: unknown): string {
  if (v == null) return ''
  return String(v)
}

export function mapMyPnlApiRow(row: MyPnlApiRow, index: number): MyPnlEventRow {
  const eventName = s(row.eventName) || '—'
  const createdAt = s(row.createdAt) || ''
  const gameName = s(row.gameName) || '—'
  const subGame = s(row.subGame) || '—'
  // Lab list `_id` is the drill eventId (BF id or event name). Two groups for
  // the same event (e.g. blank game vs SportsBook/Cricket) often share `_id`.
  // Row `id` must stay unique so Market/User injects under one parent only.
  const labKey = s(row._id).trim() || s(row.eventId).trim()
  const eventId = s(row.eventId).trim() || labKey || undefined
  const id = [labKey || eventName, gameName, subGame, createdAt, String(index)].join('::')
  return {
    id,
    settledAt: createdAt,
    gameName,
    subGame,
    eventName,
    ...(eventId ? { eventId } : {}),
    win: n(row.win),
    commission: n(row.commission),
    netAmt: n(row.netAmt),
    winInr: n(row.winINR),
    commissionInr: n(row.commissionINR),
    netAmtInr: n(row.netAmtINR),
    betFairAmount: n(row.betFairAmount),
    uplineCommission: n(row.uplineCommission),
    downlineCommission: n(row.downlineCommission),
    userCount: n(row.userCount),
    betCount: n(row.betCount),
  }
}

/** Nest Market/User drills under exactly one parent row id. */
export function injectMyPnlDrillRows(
  rows: MyPnlEventRow[],
  parentId: string,
  drills: MyPnlDrillRow[],
): MyPnlEventRow[] {
  if (!parentId || drills.length === 0) return rows
  const out: MyPnlEventRow[] = []
  for (const row of rows) {
    out.push(row)
    if (row.drill || row.id !== parentId) continue
    for (const d of drills) out.push(mapMyPnlDrillAsEventRow(d, row.id))
  }
  return out
}

function isStandardOk(response: { status?: number; value?: boolean }): boolean {
  if (response.status != null) return response.status === 200
  return response.value === true
}

export function mapMyPnlList(response: MyPnlListResponse): {
  rows: MyPnlEventRow[]
  total: number
  pageSum: MyPnlSum
  maxRow: number
} {
  if (!isStandardOk(response)) {
    return { rows: [], total: 0, pageSum: { ...ZERO_SUM }, maxRow: MY_PNL_PAGE_SIZE }
  }
  const bag = response.data
  const raw = bag?.results ?? []
  const rows = raw.map((r, i) => mapMyPnlApiRow(r, i))
  return {
    rows,
    total: n(bag?.totalItems) || rows.length,
    pageSum: bag?.totalSum ?? { ...ZERO_SUM },
    maxRow: n(bag?.maxRow) || MY_PNL_PAGE_SIZE,
  }
}

/** @deprecated Old page uses list `totalSum`; kept for tests / callers. */
export type MyPnlGrandTotalResponse = {
  value?: boolean
  status?: number
  data: MyPnlSum
}

export function mapMyPnlGrandTotal(response: MyPnlGrandTotalResponse): MyPnlSum {
  if (!isStandardOk(response)) return { ...ZERO_SUM }
  if (!response.data) return { ...ZERO_SUM }
  const d = response.data
  return {
    win: n(d.win),
    commission: n(d.commission),
    netAmt: n(d.netAmt),
    winINR: n(d.winINR),
    commissionINR: n(d.commissionINR),
    netAmtINR: n(d.netAmtINR),
    betFairAmount: n(d.betFairAmount),
  }
}

/** Lab getEventsForPLL → `{ value, data: string[] }` (event names). */
export function mapMyPnlEventOptions(response: {
  value?: boolean
  status?: number
  data:
    | string[]
    | {
        results: Array<{
          EVENTID?: string
          EVENTNAME?: string
          eventId?: string
          eventName?: string
          HAS_EVENT_ID?: string | number
        }>
      }
}): MyPnlEventOption[] {
  const opts: MyPnlEventOption[] = [{ value: 'All', label: 'All' }]
  // getEventsForPLL stays Lab `{ value, data }`; aggregator list uses Standard status.
  if (response.status != null && response.status !== 200) return opts
  if (response.value === false) return opts

  if (Array.isArray(response.data)) {
    const seen = new Set<string>()
    for (const name of response.data) {
      const label = s(name).trim()
      if (!label || label === 'All') continue
      const value = `${MY_PNL_EVENT_NAME_PREFIX}${label}`
      if (seen.has(value)) continue
      seen.add(value)
      opts.push({ value, label })
    }
    return opts
  }

  const seen = new Set<string>()
  for (const r of response.data?.results ?? []) {
    const name = s(r.EVENTNAME ?? r.eventName)
    if (!name) continue
    const rawId = s(r.EVENTID ?? r.eventId)
    const value =
      r.HAS_EVENT_ID === '0' || r.HAS_EVENT_ID === 0
        ? `${MY_PNL_EVENT_NAME_PREFIX}${name}`
        : rawId || `${MY_PNL_EVENT_NAME_PREFIX}${name}`
    if (seen.has(value)) continue
    seen.add(value)
    opts.push({ value, label: name })
  }
  return opts
}

/**
 * When getEventsForPLL is empty, keep Select Event Name usable from loaded
 * getMasterDashboardAggregator rows.
 */
export function mergeMyPnlEventOptions(
  apiOpts: MyPnlEventOption[],
  rows: Array<Pick<MyPnlEventRow, 'eventId' | 'eventName' | 'drill'>>,
): MyPnlEventOption[] {
  const opts = apiOpts.length > 0 ? [...apiOpts] : [{ value: 'All', label: 'All' }]
  if (!opts.some((o) => o.value === 'All')) opts.unshift({ value: 'All', label: 'All' })
  const seen = new Set(opts.map((o) => o.value))
  for (const r of rows) {
    if (r.drill) continue
    const name = r.eventName?.trim()
    if (!name || name === 'All') continue
    const id = r.eventId?.trim()
    const value = id && id !== 'All' ? id : `${MY_PNL_EVENT_NAME_PREFIX}${name}`
    if (seen.has(value)) continue
    seen.add(value)
    opts.push({ value, label: name })
  }
  return opts
}

export function mapDrillRow(r: MyPnlApiRow, index: number): MyPnlDrillRow {
  // Aggregate-API drill maps match/user into eventName; Lab market uses matchName.
  const label =
    s(r.matchName || r.username || r.eventName || r._id).trim() || `row-${index}`
  return {
    id: `${label}-${index}`,
    label,
    eventName: s(r.eventName),
    win: n(r.win),
    commission: n(r.commission),
    netAmt: n(r.netAmt),
    winInr: n(r.winINR),
    commissionInr: n(r.commissionINR),
    netAmtInr: n(r.netAmtINR),
    betFairAmount: n(r.betFairAmount),
    uplineCommission: n(r.uplineCommission),
    downlineCommission: n(r.downlineCommission),
    betCount: n(r.betCount),
    userCount: n(r.userCount),
  }
}

const EMPTY_LIST = {
  rows: [] as MyPnlEventRow[],
  total: 0,
  pageSum: { ...ZERO_SUM },
  maxRow: MY_PNL_PAGE_SIZE,
}

/** Lab Aggregate My P/L (old) — `POST /v1/account/getMasterDashboardAggregator`. */
export async function fetchMyPnlList(filter: MyPnlFilter) {
  if (!hasLiveApi()) return { ...EMPTY_LIST, pageSum: { ...ZERO_SUM } }
  const response = await apiFetch<MyPnlListResponse>(
    '/v1/account/getMasterDashboardAggregator',
    {
      method: 'POST',
      body: JSON.stringify(myPnlRequestBody(filter)),
    },
  )
  return mapMyPnlList(response)
}

/**
 * Old page has no separate grand-total call — Lab uses list `totalSum`.
 * Kept as a thin alias so hooks/page can stay stable.
 */
export async function fetchMyPnlGrandTotal(filter: MyPnlFilter): Promise<MyPnlSum> {
  const list = await fetchMyPnlList({ ...filter, page: 1 })
  return list.pageSum
}

export async function fetchMyPnlEventOptions(filter: MyPnlFilter) {
  if (!hasLiveApi()) return [{ value: 'All', label: 'All' }]
  const subGame = filter.subGame?.trim() || 'All'
  const body: Record<string, unknown> = {
    fromDate: filter.from.slice(0, 10),
    toDate: filter.to.slice(0, 10),
    game: labMyPnlGameId(subGame),
  }
  if (subGame && subGame !== 'All') body.subGame = subGame
  const response = await apiFetch<{ value: boolean; data: string[] }>(
    '/v1/account/getEventsForPLL',
    { method: 'POST', body: JSON.stringify(body) },
  )
  return mapMyPnlEventOptions(response)
}

/**
 * Lab `getMarketWithinEventAggregator` / `getUsersWithinEventAggregator` body.
 * Old Aggregate master-dashboard sends dates + `eventId` (= list row `_id`) +
 * typeName only — no game/subGame for sports. Also send `eventName` so the API
 * can OR-match name when `_id` is a BF id but settlements only have the name.
 * CardGame / LiveCasino: flags + eventName so Aggregate-API can parse the day.
 */
export function myPnlDrillRequestBody(
  filter: MyPnlFilter,
  row: Pick<MyPnlEventRow, 'id' | 'eventId' | 'eventName' | 'subGame'>,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    fromDate: filter.from.slice(0, 10),
    toDate: filter.to.slice(0, 10),
  }
  const typeName = filter.typeName?.trim()
  if (typeName && typeName !== 'All') body.typeName = typeName

  const eventName = row.eventName?.trim() || ''
  // Lab: `eventId: event._id` — BF event id, or event name when id is absent.
  const labEventKey = (row.eventId?.trim() || row.id?.trim() || '').trim()
  if (labEventKey && labEventKey !== 'All') body.eventId = labEventKey
  if (eventName && eventName !== 'All') body.eventName = eventName

  const card = eventName.includes('CardGame') || row.subGame === 'Card Game'
  const casino = eventName.includes('LiveCasino') || row.subGame === 'Live Casino'
  if (card) {
    body.isCardGame = true
    body.game = LAB_MY_PNL_GAME_IDS.cardGame
    body.subGame = 'Card Game'
  }
  if (casino) {
    body.isLiveCasino = true
    body.game = LAB_MY_PNL_GAME_IDS.liveCasino
    body.subGame = 'Live Casino'
  }
  return body
}

/**
 * Peel nested Standard envelopes until `data` is the drill row array.
 * UAT has been observed returning `{ status, message, data: { status, message, data: [] } }`.
 */
export function unwrapMyPnlDrillRows(response: MyPnlDrillResponse): MyPnlApiRow[] {
  if (!isStandardOk(response)) return []
  let cur: unknown = response.data
  for (let i = 0; i < 3; i++) {
    if (Array.isArray(cur)) return cur
    if (
      cur &&
      typeof cur === 'object' &&
      'data' in cur &&
      (cur as { status?: number }).status === 200
    ) {
      cur = (cur as { data: unknown }).data
      continue
    }
    break
  }
  return []
}

export function mapMyPnlDrillAsEventRow(d: MyPnlDrillRow, parentId: string): MyPnlEventRow {
  return {
    id: `drill-${parentId}-${d.id}`,
    settledAt: '',
    gameName: '',
    subGame: '',
    eventName: d.label,
    win: d.win,
    commission: d.commission,
    netAmt: d.netAmt,
    winInr: d.winInr,
    commissionInr: d.commissionInr,
    netAmtInr: d.netAmtInr,
    betFairAmount: d.betFairAmount,
    uplineCommission: d.uplineCommission,
    downlineCommission: d.downlineCommission,
    userCount: d.userCount,
    betCount: d.betCount,
    drill: true,
    drillParentId: parentId,
  }
}

export type MyPnlDrillResponse = {
  status?: number
  message?: string
  value?: boolean
  data: MyPnlApiRow[] | MyPnlDrillResponse
}

export async function fetchMyPnlMarkets(
  filter: MyPnlFilter,
  row: Pick<MyPnlEventRow, 'id' | 'eventId' | 'eventName' | 'subGame'>,
) {
  if (!hasLiveApi()) return []
  const response = await apiFetch<MyPnlDrillResponse>(
    '/v1/account/getMarketWithinEventAggregator',
    {
      method: 'POST',
      body: JSON.stringify(myPnlDrillRequestBody(filter, row)),
    },
  )
  return unwrapMyPnlDrillRows(response).map(mapDrillRow)
}

export async function fetchMyPnlUsers(
  filter: MyPnlFilter,
  row: Pick<MyPnlEventRow, 'id' | 'eventId' | 'eventName' | 'subGame'>,
) {
  if (!hasLiveApi()) return []
  const response = await apiFetch<MyPnlDrillResponse>(
    '/v1/account/getUsersWithinEventAggregator',
    {
      method: 'POST',
      body: JSON.stringify(myPnlDrillRequestBody(filter, row)),
    },
  )
  return unwrapMyPnlDrillRows(response).map(mapDrillRow)
}
